<template>
  <section class="employee-checkpoint" aria-label="Complete checkpoint">
    <AsEmployeeCompleteCheckpointRightPaneHeader
      :title="title"
      :employee-name="employeeName"
      :first-name="firstName"
      :last-name="lastName"
      :job-title="jobTitle"
      :avatar="avatar"
      :level="level"
      :criteria="criteria"
      :responsibility-name="responsibilityName"
      :responsibility-code="responsibilityCode"
      :variant="coachStyleHeaderEnabled ? 'coach-style' : 'classic'"
      @close="emit('close')"
    />

    <div class="employee-checkpoint__navigator">
      <div class="employee-checkpoint__navigator-main">
        <StandardButton
          v-if="!isFirstStep && !isResult"
          type="magenta"
          variant="outline"
          size="small"
          label="Back"
          left-icon="chevronLeftBack"
          @click="handlePrevious"
        />
        <p
          class="employee-checkpoint__section-title"
          :class="{ 'employee-checkpoint__section-title--note': isInspectorOnly }"
        >
          {{ navigatorTitle }}
        </p>
        <div v-if="normalizedSections.length > 1" class="employee-checkpoint__steps">
          <WizardSteps
            size="small"
            :count_of_steps="normalizedSections.length"
            :current_step="clampedActiveStep"
          />
        </div>
      </div>

      <StandardButton
        v-if="!isInspectorOnly"
        type="magenta"
        :variant="actionDisabled ? 'outline' : 'primary'"
        size="small"
        :label="actionLabel"
        :right-icon="showActionChevron ? 'chevronRightForward' : null"
        :disabled="actionDisabled"
        :class="{ 'employee-checkpoint__next--disabled': actionDisabled }"
        @click="handleAction"
      />
    </div>

    <div v-if="isQuiz || isResult" class="employee-checkpoint__quiz-alert">
      <AsEmployeeQuizProgressAlert
        v-bind="progressAlertProps"
        :disabled="disabled"
        @submit="handleQuizSubmit"
      />
    </div>

    <div v-if="isInspectorOnly" class="employee-checkpoint__content">
      <CheckpointPrepNoteBlock :note="currentSection.employeePrepNote ?? null" />
    </div>

    <div
      v-else
      class="employee-checkpoint__body"
      :class="{ 'employee-checkpoint__body--lesson-card': lessonStepCardActive }"
    >
      <slot name="content" :section="currentSection">
        <component
          :is="lessonStepWrapper"
          v-if="isLearningStep"
          :type="currentSection.type"
          :step-index="clampedActiveStep"
          :step-total="normalizedSections.length"
          :seen="lessonSeen"
        >
          <div v-if="isTextContent" class="employee-checkpoint__article">
            <h3 v-if="currentSection.heading" class="employee-checkpoint__article-heading">
              {{ currentSection.heading }}
            </h3>
            <InstructionsLanguagePicker
              v-if="hasArticleLanguages"
              :model-value="currentSection.selectedLanguage || null"
              :items="currentSection.languages || []"
              :isforce-readonly="true"
              :is-show-defaut-value="true"
              @update:model-value="emit('update:selected-language', $event)"
              @item-language-selected="emit('language-selected', $event)"
            />

            <div v-if="hasArticleEditorContent" class="employee-checkpoint__article-editor">
              <exiq-notion-editor
                ref="notionEditorHost"
                :key="articleEditorInstanceKey"
                :content.prop="notionContentDoc"
                :value.prop="notionValueProp"
                :readonly.prop="true"
                :link-button.prop="linkButtonEnabled"
                :show-character-count="false"
                :show-table-of-contents="true"
              />
            </div>
            <div v-else class="employee-checkpoint__empty">
              {{ textContentPlaceholder }}
            </div>
          </div>

          <!-- Video: VideoWithChaptersView (MDP-578) or legacy MicrolearningViewEdit. -->
          <VideoWithChaptersView
            v-else-if="usesVideoWithChaptersView"
            class="employee-checkpoint__video-view"
            :video-url="videoPlaybackUrl"
            :duration-seconds="videoWithChaptersDurationSeconds"
            :chapters="videoWithChaptersForView"
            :summary-lead="videoWithChaptersSummaryLead"
            :summary-bullets="videoWithChaptersSummaryBullets"
            :editable="false"
          />
          <MicrolearningViewEdit
            v-else
            :video="videoPlaybackUrl"
            :error="currentSection.error || null"
            :summary="currentSection.summary || null"
            :steps="currentSection.steps || []"
            :editable="false"
            :show-steps="currentSection.showSteps === true"
            :max-steps-height="currentSection.maxStepsHeight || '460px'"
          />
        </component>

        <div v-else-if="isQuiz" class="employee-checkpoint__quiz">
          <AsEmployeeQuizQuestionCard
            v-for="question in normalizedQuestions"
            :key="question.key"
            :question="question.text"
            :answers="question.answers"
            :result="question.result"
            :result-message="question.resultMessage"
            :readonly="question.isLocked"
            :helper-state="question.isAnswered ? 'default' : 'error'"
            :v2="quizCardV2Enabled"
            :index="question.position"
            :total="normalizedQuestions.length"
            @answer-selected="handleAnswerChange(question.id, $event)"
          />

          <div v-if="normalizedQuestions.length === 0" class="employee-checkpoint__empty">
            No quiz questions are available.
          </div>
        </div>

        <div v-else-if="isResult" class="employee-checkpoint__result">
          <CheckpointResultCard
            :question-count="resultQuestionCount"
            :level="level"
            :step-label="stepLabel"
          />
        </div>

        <div v-else-if="isInspection" class="employee-checkpoint__inspection">
          <div
            v-if="inspectionCardV2Active"
            class="employee-checkpoint__inspection-summary"
            data-testid="employee-inspection-summary"
          >
            <div class="employee-checkpoint__inspection-progress">
              <InspectionProgress :states="inspectionStepStates" />
              <p
                v-if="inspectionHelperText"
                class="employee-checkpoint__inspection-helper"
                data-testid="employee-inspection-helper"
              >
                {{ inspectionHelperText }}
              </p>
            </div>

            <div
              v-if="showAvailableInspectors"
              class="employee-checkpoint__inspection-people"
              aria-label="Available inspectors"
              data-testid="employee-available-inspectors"
            >
              <span>Available Inspectors:</span>
              <PeopleAvatarsButton
                :avatars="inspectors"
                :max-visible="3"
                size="small"
                :is-hover-enable="false"
                :is-show-tooltip="true"
                :allow-drill-down="false"
                @onAvatarClick="emit('inspectors-click')"
              />
            </div>
            <div
              v-else-if="showInspectedBy"
              class="employee-checkpoint__inspection-people"
              data-testid="employee-inspected-by"
            >
              <ExAvatar size="small" :first-name="inspectorFirstName" :last-name="inspectorLastName" />
              <span>{{ inspectedByText }}</span>
            </div>
          </div>
          <AsEmployeeInspectionStatusAlert
            v-else
            :status="inspectionStatus"
            :title="inspectionTitle"
            :description="inspectionDescription"
            :inspector-name="inspectorName"
            :inspection-date="inspectionDate"
            :inspectors="inspectors"
            @inspectors-click="emit('inspectors-click')"
          />

          <div class="employee-checkpoint__inspection-steps">
            <template v-if="inspectionCardV2Active">
              <InstructionStepCardV2
                v-for="step in inspectionSteps"
                :key="step.key"
                mode="employee"
                :category="step.competenceCategory"
                :inspector-action="step.inspectorAction"
                :look-for="step.lookFor"
                :pass-criteria="step.passCriteria"
                :fail-criteria="step.failCriteria"
                :timestamp-ref="step.timestamp"
                :video-jump-url="step.videoJumpUrl"
                :step-index="step.position"
                :step-count="inspectionSteps.length"
                :result="step.result"
                :comment="step.comment"
                :inspected-by-name="step.inspectedByName"
                :inspected-at="step.inspectedAt"
                @timestamp-click="openVideoPlayer"
              />
            </template>
            <template v-else>
              <InstructionStepCard
                v-for="step in inspectionSteps"
                :key="step.key"
                :competence_category="step.competenceCategory"
                :inspector_action="step.inspectorAction"
                :look_for="step.lookFor"
                :pass_criteria="step.passCriteria"
                :fail_criteria="step.failCriteria"
                :timestamp_ref="step.timestamp"
                :video_jump_url="step.videoJumpUrl"
                :read-only="true"
                @timestamp-click="openVideoPlayer"
              />
            </template>
          </div>

          <div v-if="inspectionSteps.length === 0" class="employee-checkpoint__empty">
            No inspection instructions are available.
          </div>
        </div>

        <div v-else class="employee-checkpoint__empty">No checkpoint content is available.</div>
      </slot>
    </div>

    <v-dialog
      v-model="showVideoPlayer"
      max-width="680px"
      class="employee-checkpoint__video-dialog"
      @click:outside="closeVideoPlayer"
    >
      <v-card class="employee-checkpoint__video-card">
        <v-card-title class="employee-checkpoint__video-title">
          <span class="employee-checkpoint__video-title-text">Watch Video</span>
          <v-spacer />
          <v-btn
            icon
            aria-label="Close video dialog"
            class="employee-checkpoint__video-close"
            @click="closeVideoPlayer"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="employee-checkpoint__video-content">
          <div v-if="videoSrc" class="employee-checkpoint__video-iframe-wrap">
            <iframe
              :key="videoIframeKey"
              :src="videoSrc"
              title="Checkpoint microlearning video"
              class="employee-checkpoint__video-iframe"
              allow="
                accelerometer;
                autoplay;
                clipboard-write;
                encrypted-media;
                gyroscope;
                picture-in-picture;
                fullscreen;
              "
              allowfullscreen
              frameborder="0"
            />
          </div>
          <div v-else class="employee-checkpoint__video-empty">
            <v-icon size="64" color="grey">mdi-video-off</v-icon>
            <p>No video content available</p>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </section>
</template>

<script setup>
import { computed, getCurrentInstance, nextTick, onMounted, ref, watch } from "vue";
import { getCheckpointVideoSignedUrl, postCheckpointLearningEvent } from "@/api/responsibilityAPI";
import {
  isApiVideoPlaybackUrl,
  needsSignedVideoUrlResolution,
} from "@/utils/activeCheckpointMicrolearningValidity";
import { isCloudflareSignedUrl } from "@/utils/simulatedCloudflareStreamWebhook";
import AsEmployeeCompleteCheckpointRightPaneHeader from "@components/AsEmployeeCompleteCheckpointRightPaneHeader/AsEmployeeCompleteCheckpointRightPaneHeader.vue";
import AsEmployeeInspectionStatusAlert from "@components/AsEmployeeInspectionStatusAlert/AsEmployeeInspectionStatusAlert.vue";
import AsEmployeeQuizProgressAlert from "@components/AsEmployeeQuizProgressAlert/AsEmployeeQuizProgressAlert.vue";
import AsEmployeeQuizQuestionCard from "@components/AsEmployeeQuizQuestionCard/AsEmployeeQuizQuestionCard.vue";
import CheckpointResultCard from "@components/CheckpointResultCard/CheckpointResultCard.vue";
import CheckpointPrepNoteBlock from "@components/CheckpointPrepNoteBlock/CheckpointPrepNoteBlock.vue";
import ExAvatar from "@components/common/ExAvatar.vue";
import InspectionProgress from "@components/InspectionProgress/InspectionProgress.vue";
import InstructionStepCard from "@components/InstructionStepCard/InstructionStepCard.vue";
import InstructionStepCardV2 from "@components/InstructionStepCardV2/InstructionStepCardV2.vue";
import InstructionsLanguagePicker from "@components/InstructionsLanguagePicker/InstructionsLanguagePicker.vue";
import LessonStepCard from "@components/LessonStepCard/LessonStepCard.vue";
import MicrolearningViewEdit from "@components/MicrolearningViewEdit/MicrolearningViewEdit.vue";
import PeopleAvatarsButton from "@components/PeopleAvatarsButton/PeopleAvatarsButton.vue";
import VideoWithChaptersView from "@components/VideoWithChaptersView/VideoWithChaptersView.vue";
import {
  LD_FLAG_IDEA_MDP_604,
  LD_FLAG_IDEA_MDP_649,
  LD_FLAG_VIDEO_WITH_CHAPTERS_VIEW,
} from "@components/constants/launchDarklyFlagKeys";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import WizardSteps from "@components/WizardSteps/WizardSteps.vue";
import {
  isArticleDocVisuallyEmpty,
  isArticleHtmlVisuallyEmpty,
  paragraphsToArticleHtml,
  resolveNotionArticleContent,
} from "@components/utils/formatArticleHtml";
import "@components/webcomponents-dist/slash-menu-global.css";
import "@components/webcomponents-dist/notion-editor-date-patch.js";
import "@components/webcomponents-dist/notion-editor-modals-patch.js";
import "@components/webcomponents-dist/notion-editor-slash-patch.js";

const props = defineProps({
  title: { type: String, default: "Complete Checkpoint" },
  employeeName: { type: String, default: "" },
  firstName: { type: String, default: "" },
  lastName: { type: String, default: "" },
  jobTitle: { type: String, default: "" },
  avatar: { type: [String, Object], default: null },
  level: {
    type: String,
    default: "Apprentice",
    validator: (value) => ["Apprentice", "Professional", "Coach", "Master"].includes(value),
  },
  criteria: { type: String, default: "" },
  responsibilityName: { type: String, default: "" },
  responsibilityCode: { type: String, default: "" },
  sections: {
    type: Array,
    default: () => [],
    validator: (sections) =>
      sections.every((section) =>
        ["article", "video", "quiz", "result", "inspection", "inspector-only"].includes(
          String(section.type).toLowerCase()
        )
      ),
  },
  currentStep: { type: Number, default: 1 },
  checkpointId: { type: [Number, String], default: null },
  responsibilityId: { type: [Number, String], default: null },
  selectedAnswers: { type: Object, default: () => ({}) },
  inspectors: { type: Array, default: () => [] },
  inspectionStatus: {
    type: String,
    default: "pending",
    validator: (value) => ["pending", "passed", "failed", "not_applicable"].includes(value),
  },
  inspectionTitle: { type: String, default: "" },
  inspectionDescription: { type: String, default: "" },
  inspectorName: { type: String, default: "" },
  inspectionDate: { type: String, default: "" },
  disabled: { type: Boolean, default: false },
  /**
   * Review of a completed checkpoint (article read / quiz passed).
   * Quiz is locked and Next is not gated on a new attempt.
   */
  readonly: { type: Boolean, default: false },
  /** Server-graded quiz attempt result from submit-quiz-attempt API. */
  quizAttemptResult: { type: Object, default: null },
  /** `learning_events` of the employee checkpoint payload (e.g. "video_seen", "article_seen"). */
  learningEvents: { type: Array, default: () => [] },
  employeeId: { type: [Number, String], default: null },
  /** False while the host drawer is closed; learning events are only recorded once it is open. */
  visible: { type: Boolean, default: true },
});

const { proxy } = getCurrentInstance() || {};

/** MDP-578 — learner-style video + TOC (replaces MicrolearningViewEdit when on). */
const videoWithChaptersViewEnabled = ref(false);

/** MDP-604 — render a lone link in the article as a button. */
const linkButtonEnabled = ref(false);

/** MDP-649 — coach-style header (responsibility chip + criteria row). */
const coachStyleHeaderEnabled = ref(false);

onMounted(async () => {
  try {
    coachStyleHeaderEnabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_649, false)) === true;
  } catch {
    coachStyleHeaderEnabled.value = false;
  }
});

onMounted(async () => {
  try {
    linkButtonEnabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_604, true)) !== false;
  } catch {
    linkButtonEnabled.value = true;
  }
});

/** MDP-649 — video/article steps inside LessonStepCard, with Video seen / Article seen events. */
const lessonStepCardEnabled = ref(false);

onMounted(async () => {
  try {
    lessonStepCardEnabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_649, false)) === true;
  } catch {
    lessonStepCardEnabled.value = false;
  }
});

/** MDP-649 — quiz question cards in the As a Coach inspection style. Fail closed. */
const quizCardV2Enabled = ref(false);

onMounted(async () => {
  try {
    quizCardV2Enabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_649, false)) === true;
  } catch {
    quizCardV2Enabled.value = false;
  }
});

/** MDP-649 — per-step inspection cards and progress in the As a Coach style. Fail closed. */
const inspectionCardV2Enabled = ref(false);

onMounted(async () => {
  try {
    inspectionCardV2Enabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_649, false)) === true;
  } catch {
    inspectionCardV2Enabled.value = false;
  }
});

onMounted(async () => {
  try {
    videoWithChaptersViewEnabled.value =
      (await proxy?.$getFlag?.(LD_FLAG_VIDEO_WITH_CHAPTERS_VIEW, true)) === true;
  } catch {
    videoWithChaptersViewEnabled.value = true;
  }
});

const emit = defineEmits([
  "close",
  "previous",
  "next",
  "complete",
  "quiz-submit",
  "answer-change",
  "inspectors-click",
  "update:current-step",
  "update:selected-answers",
  "update:selected-language",
  "language-selected",
  "timestamp-click",
  "learning-event-recorded",
]);

const activeStep = ref(Math.max(props.currentStep, 1));
const localAnswers = ref({ ...props.selectedAnswers });
const quizSubmitted = ref(false);
const questionVerdicts = ref({});
const showVideoPlayer = ref(false);
const videoSrc = ref(null);
const videoIframeKey = ref(0);
const resolvedMicrolearningVideoUrl = ref("");

function resolveVideoSrc(video) {
  if (!video || typeof video !== "string") return null;

  if (video.includes("<iframe") || video.includes("src=")) {
    const srcMatch = video.match(/src=["']([^"']+)["']/);
    if (srcMatch?.[1]) return srcMatch[1];
  }

  if (
    video.includes("cloudflarestream.com") ||
    video.includes("videodelivery.net") ||
    isApiVideoPlaybackUrl(video) ||
    /^https?:\/\//i.test(video)
  ) {
    return video;
  }

  return null;
}

function appendAutoplayToVideoUrl(url) {
  if (!url || url.includes("autoplay=")) return url;
  const separator = url.includes("?") ? "&" : "?";
  if (url.includes("#")) {
    const hashIdx = url.indexOf("#");
    const baseUrl = url.slice(0, hashIdx);
    const hash = url.slice(hashIdx + 1);
    return `${baseUrl}${separator}autoplay=true#${hash}`;
  }
  return `${url}${separator}autoplay=true`;
}

function closeVideoPlayer() {
  showVideoPlayer.value = false;
  videoSrc.value = null;
}

function openVideoPlayer(videoJumpUrl) {
  const resolved = resolveVideoSrc(videoJumpUrl);
  if (!resolved) return;
  videoSrc.value = appendAutoplayToVideoUrl(resolved);
  videoIframeKey.value += 1;
  showVideoPlayer.value = true;
  emit("timestamp-click", videoJumpUrl);
}

watch(
  () => props.currentStep,
  (value) => {
    activeStep.value = Math.max(value, 1);
  }
);

watch(
  () => props.selectedAnswers,
  (value) => {
    localAnswers.value = { ...value };
  },
  { deep: true }
);

watch(activeStep, () => {
  quizSubmitted.value = false;
  questionVerdicts.value = {};
  closeVideoPlayer();
});

function applyServerQuizResult(result) {
  const questions =
    result?.inspection?.quiz_result?.questions || result?.quiz_result?.questions || [];
  if (!questions.length) return;

  const verdicts = { ...questionVerdicts.value };
  const nextAnswers = { ...localAnswers.value };

  questions.forEach((question, index) => {
    const id = mappedQuestions.value[index]?.id;
    if (!id) return;

    const isCorrect = question?.user_response?.is_correct_judgment === true;
    verdicts[id] = isCorrect ? "correct" : "incorrect";
    if (!isCorrect) nextAnswers[id] = [];
  });

  questionVerdicts.value = verdicts;
  localAnswers.value = nextAnswers;
  quizSubmitted.value = true;
}

function isServerQuizPassed(result) {
  const questions =
    result?.inspection?.quiz_result?.questions || result?.quiz_result?.questions || [];
  return (
    questions.length > 0 &&
    questions.every((question) => question?.user_response?.is_correct_judgment === true)
  );
}

/** The pass is recorded by the submit call; a quiz-only checkpoint then moves on to its result. */
function advanceToResultAfterPass(result) {
  if (!isQuiz.value || !isServerQuizPassed(result)) return;
  if (normalizedSections.value[clampedActiveStep.value]?.type !== "result") return;
  goToNextStep();
}

watch(
  () => props.quizAttemptResult,
  (result) => {
    if (!result) return;
    applyServerQuizResult(result);
    advanceToResultAfterPass(result);
  }
);

const normalizedSections = computed(() =>
  props.sections.map((section, index) => ({
    ...section,
    id: section.id ?? `${section.type}-${index + 1}`,
    type: String(section.type).toLowerCase(),
    title: section.title || defaultSectionTitle(section.type),
  }))
);

const clampedActiveStep = computed(() =>
  Math.min(Math.max(activeStep.value, 1), Math.max(normalizedSections.value.length, 1))
);

const currentSection = computed(
  () =>
    normalizedSections.value[clampedActiveStep.value - 1] || {
      id: "empty",
      type: "empty",
      title: "Checkpoint",
    }
);

const isFirstStep = computed(() => clampedActiveStep.value === 1);
const isLastStep = computed(() => clampedActiveStep.value >= normalizedSections.value.length);
const navigatorTitle = computed(
  () => currentSection.value.navigatorTitle || currentSection.value.title
);
const showActionChevron = computed(() => actionLabel.value === "Next");
const isArticle = computed(() => currentSection.value.type === "article");
const isVideo = computed(() => currentSection.value.type === "video");
const usesVideoWithChaptersView = computed(
  () =>
    isVideo.value &&
    videoWithChaptersViewEnabled.value &&
    currentSection.value.isStepByStepVideo !== true
);
const isQuiz = computed(() => currentSection.value.type === "quiz");
const isResult = computed(() => currentSection.value.type === "result");
const isInspection = computed(() => currentSection.value.type === "inspection");
const isInspectorOnly = computed(() => currentSection.value.type === "inspector-only");
const isTextContent = computed(() => isArticle.value);
const isLearningStep = computed(() => isArticle.value || isVideo.value);

/** Flag off: renders the step content directly, exactly as before the card existed. */
const LessonStepPassthrough = { functional: true, render: (h, context) => context.children };
const lessonStepWrapper = computed(() =>
  lessonStepCardEnabled.value ? LessonStepCard : LessonStepPassthrough
);
const lessonStepCardActive = computed(() => lessonStepCardEnabled.value && isLearningStep.value);

const LEARNING_EVENT_BY_TYPE = { video: "video_seen", article: "article_seen" };
const recordedLearningKeys = ref([]);
const requestedLearningKeys = new Set();

const payloadLearningEvents = computed(
  () =>
    new Set(
      props.learningEvents
        .map((entry) => (typeof entry === "string" ? entry : entry?.event))
        .filter(Boolean)
    )
);

const learningEventKey = (checkpointId, event) => `${checkpointId}:${event}`;

function isLearningEventSeen(event) {
  return (
    payloadLearningEvents.value.has(event) ||
    recordedLearningKeys.value.includes(learningEventKey(props.checkpointId, event))
  );
}

const lessonSeen = computed(() =>
  isLearningEventSeen(LEARNING_EVENT_BY_TYPE[currentSection.value.type])
);

const learningEventsToRecord = computed(() => [
  ...new Set(
    normalizedSections.value.map((section) => LEARNING_EVENT_BY_TYPE[section.type]).filter(Boolean)
  ),
]);

async function recordLearningEvents() {
  if (!lessonStepCardEnabled.value || !props.visible) return;

  let accountId = null;
  try {
    accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  } catch {
    return;
  }
  const { responsibilityId, employeeId, checkpointId } = props;
  if ([accountId, responsibilityId, employeeId, checkpointId].some((value) => value == null)) {
    return;
  }

  for (const event of learningEventsToRecord.value) {
    const key = learningEventKey(checkpointId, event);
    if (isLearningEventSeen(event) || requestedLearningKeys.has(key)) continue;

    requestedLearningKeys.add(key);
    try {
      await postCheckpointLearningEvent(accountId, responsibilityId, employeeId, checkpointId, {
        event,
      });
      recordedLearningKeys.value = [...recordedLearningKeys.value, key];
      emit("learning-event-recorded", event);
    } catch (error) {
      requestedLearningKeys.delete(key);
      console.error("Error recording checkpoint learning event:", error);
    }
  }
}

watch(
  () => [
    lessonStepCardEnabled.value,
    props.visible,
    props.checkpointId,
    props.employeeId,
    learningEventsToRecord.value.join(","),
  ],
  recordLearningEvents,
  { immediate: true }
);

function isBareCloudflareStreamUid(value) {
  return typeof value === "string" && /^[a-f0-9]{32}$/i.test(value.trim());
}

function isCloudflareStreamPlaybackUrl(value) {
  if (!value || typeof value !== "string") return false;
  const trimmed = value.trim();
  return (
    /cloudflarestream\.com\/[^/?#]+\/iframe/i.test(trimmed) ||
    trimmed.includes("iframe.videodelivery.net")
  );
}

function resolvePlaybackCandidate(video) {
  const resolved = resolveVideoSrc(video);
  if (resolved) return resolved;
  if (video && !isBareCloudflareStreamUid(video)) return video;
  return "";
}

async function refreshMicrolearningVideoUrl() {
  if (!isVideo.value) {
    resolvedMicrolearningVideoUrl.value = "";
    return;
  }

  const sectionUrl = currentSection.value.videoUrl || "";
  if (
    sectionUrl &&
    !isBareCloudflareStreamUid(sectionUrl) &&
    (sectionUrl.toLowerCase().includes("<iframe") ||
      isApiVideoPlaybackUrl(sectionUrl) ||
      isCloudflareStreamPlaybackUrl(sectionUrl) ||
      isCloudflareSignedUrl(sectionUrl) ||
      (!needsSignedVideoUrlResolution(sectionUrl, isCloudflareSignedUrl) &&
        /^https?:\/\//i.test(sectionUrl)))
  ) {
    resolvedMicrolearningVideoUrl.value = resolvePlaybackCandidate(sectionUrl);
    return;
  }

  if (props.checkpointId == null || props.responsibilityId == null) {
    resolvedMicrolearningVideoUrl.value = resolvePlaybackCandidate(sectionUrl);
    return;
  }

  try {
    const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
    if (!accountId) {
      resolvedMicrolearningVideoUrl.value = resolvePlaybackCandidate(sectionUrl);
      return;
    }
    const { data } = await getCheckpointVideoSignedUrl(
      accountId,
      props.responsibilityId,
      props.checkpointId
    );
    resolvedMicrolearningVideoUrl.value = data?.url || resolvePlaybackCandidate(sectionUrl);
  } catch (error) {
    console.error("Error fetching checkpoint video signed URL:", error);
    resolvedMicrolearningVideoUrl.value = resolvePlaybackCandidate(sectionUrl);
  }
}

const videoPlaybackUrl = computed(
  () => resolvedMicrolearningVideoUrl.value || currentSection.value.videoUrl || ""
);

function parseVideoTimestampToSeconds(value) {
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.max(0, value);
  }
  const raw = String(value ?? "").trim();
  if (!raw) return 0;
  const timePart = raw.split("-")[0].trim();
  const segments = timePart.split(":").map((part) => parseInt(part, 10));
  if (segments.some((part) => Number.isNaN(part))) return 0;
  if (segments.length === 3) {
    return segments[0] * 3600 + segments[1] * 60 + segments[2];
  }
  if (segments.length === 2) {
    return segments[0] * 60 + segments[1];
  }
  return segments[0] ?? 0;
}

function formatVideoTimestampLabel(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const total = Math.floor(seconds);
  const minutes = Math.floor(total / 60);
  const secs = total % 60;
  return `${minutes}:${secs.toString().padStart(2, "0")}`;
}

function resolveChapterThumbnailUrl(chapter = {}) {
  const candidate =
    chapter.thumbnail ??
    chapter.thumbnail_url ??
    chapter.thumbnailUrl ??
    chapter.chapter_thumbnail ??
    chapter.chapter_thumbnail_url ??
    "";
  return String(candidate ?? "").trim();
}

function mapDrawerVideoChapterStep(step = {}) {
  const migrated = !String(step.chapter_title ?? "").trim();
  return {
    chapter_title: migrated ? step.inspector_action ?? "" : step.chapter_title ?? "",
    chapter_description: migrated
      ? step.chapter_description ?? step.look_for ?? ""
      : step.chapter_description ?? "",
    chapter_summary: migrated
      ? step.chapter_summary ?? step.pass_criteria ?? ""
      : step.chapter_summary ?? "",
    start_time: migrated ? step.start_time ?? step.timestamp_ref ?? "" : step.start_time ?? "",
    end_time: step.end_time ?? "",
    inspector_action: step.inspector_action ?? "",
    look_for: step.look_for ?? "",
    pass_criteria: step.pass_criteria ?? "",
    thumbnail: resolveChapterThumbnailUrl(step),
    video_jump_url: step.video_jump_url ?? "",
  };
}

const videoDrawerChapterSteps = computed(() => {
  if (!isVideo.value) return [];
  const steps = currentSection.value.steps;
  if (!Array.isArray(steps)) return [];
  return steps.map((step) => mapDrawerVideoChapterStep(step));
});

const videoWithChaptersForView = computed(() =>
  videoDrawerChapterSteps.value.map((step, index) => {
    const startTimeSeconds = parseVideoTimestampToSeconds(step.start_time);
    const startTimeLabel =
      String(step.start_time ?? "").trim() || formatVideoTimestampLabel(startTimeSeconds);
    const title =
      String(step.chapter_title ?? step.inspector_action ?? "").trim() ||
      `Chapter ${index + 1}`;
    const description =
      String(
        step.chapter_description ?? step.chapter_summary ?? step.look_for ?? step.pass_criteria ?? ""
      ).trim() || title;
    const thumbnailUrl = resolveChapterThumbnailUrl(step);

    return {
      chapterNumber: index + 1,
      title,
      startTimeSeconds,
      startTimeLabel,
      description,
      thumbnailUrl,
      videoJumpUrl: String(step.video_jump_url ?? "").trim(),
    };
  })
);

const videoWithChaptersDurationSeconds = computed(() => {
  const fromSection = Number(currentSection.value.videoDurationSeconds);
  if (Number.isFinite(fromSection) && fromSection > 0) {
    return fromSection;
  }

  let maxSeconds = 0;
  for (const step of videoDrawerChapterSteps.value) {
    maxSeconds = Math.max(
      maxSeconds,
      parseVideoTimestampToSeconds(step.start_time),
      parseVideoTimestampToSeconds(step.end_time)
    );
  }
  return maxSeconds;
});

const videoWithChaptersSummaryLead = computed(() => {
  const section = currentSection.value;
  const fromSection = String(section.summaryLead ?? "").trim();
  if (fromSection) return fromSection;

  const summary = section.summary;
  if (summary && typeof summary === "object" && !Array.isArray(summary)) {
    const overview = String(summary.overview ?? summary.text ?? "").trim();
    if (overview) return overview;
  }
  if (typeof summary === "string" && summary.trim()) {
    return summary.trim();
  }
  return "";
});

const videoWithChaptersSummaryBullets = computed(() => {
  const bullets = currentSection.value.summaryBullets;
  if (!Array.isArray(bullets)) return [];
  return bullets.map((bullet) => String(bullet ?? "").trim()).filter(Boolean);
});

watch(
  () => [currentSection.value?.videoUrl, clampedActiveStep.value, props.checkpointId],
  () => {
    refreshMicrolearningVideoUrl();
  },
  { immediate: true }
);

const stepLabel = computed(
  () => `Step ${clampedActiveStep.value} of ${Math.max(normalizedSections.value.length, 1)}`
);

const resultQuestionCount = computed(() => Number(currentSection.value.questionCount) || 0);

const progressAlertProps = computed(() => {
  if (isResult.value) {
    return {
      status: "passed",
      answeredCount: resultQuestionCount.value,
      correctCount: resultQuestionCount.value,
      totalCount: resultQuestionCount.value,
      passedMessage: "Checkpoint completed",
    };
  }
  return {
    status: quizStatus.value,
    answeredCount: answeredQuestionsCount.value,
    correctCount: correctQuestionsCount.value,
    totalCount: normalizedQuestions.value.length,
  };
});

const actionLabel = computed(() => (isLastStep.value ? "Close" : "Next"));

const articleParagraphs = computed(() => {
  const content = currentSection.value.content;
  const paragraphs = Array.isArray(content)
    ? content.map((paragraph) => (typeof paragraph === "string" ? paragraph : paragraph.text || ""))
    : String(content || "").split(/\n{2,}/);

  return paragraphs.map((paragraph) => paragraph.trim()).filter(Boolean);
});

const textContentPlaceholder = computed(
  () => currentSection.value.placeholder || "No article content is available."
);

const hasArticleLanguages = computed(() => (currentSection.value.languages || []).length > 0);

const resolvedArticleViewer = computed(() => {
  const section = currentSection.value || {};
  const htmlFromParagraphs = paragraphsToArticleHtml(articleParagraphs.value);
  return resolveNotionArticleContent({
    articleHtml:
      section.articleHtml && !isArticleHtmlVisuallyEmpty(section.articleHtml)
        ? section.articleHtml
        : htmlFromParagraphs,
    articleBody: section.articleBody,
  });
});

const notionContentDoc = computed(() => resolvedArticleViewer.value.contentDoc);
const notionValueProp = computed(() =>
  resolvedArticleViewer.value.contentDoc ? "" : resolvedArticleViewer.value.valueHtml
);

const hasArticleEditorContent = computed(() => {
  const { contentDoc, valueHtml } = resolvedArticleViewer.value;
  if (contentDoc && !isArticleDocVisuallyEmpty(contentDoc)) return true;
  return !isArticleHtmlVisuallyEmpty(valueHtml);
});

const articleEditorInstanceKey = computed(
  () => `${currentSection.value.id || "article"}-${clampedActiveStep.value}`
);

const notionEditorHost = ref(null);

function isNotionCustomElementHost(el) {
  return el instanceof HTMLElement && el.tagName.toLowerCase() === "exiq-notion-editor";
}

function getNotionEditorDomHost(raw) {
  if (!raw || typeof raw !== "object") return null;
  if (isNotionCustomElementHost(raw)) return raw;
  const root = raw.$el;
  if (isNotionCustomElementHost(root)) return root;
  return null;
}

function syncNotionCustomElementProps() {
  const el = getNotionEditorDomHost(notionEditorHost.value);
  if (!el) return;
  const doc = notionContentDoc.value;
  const html = notionValueProp.value;
  if (doc && typeof doc === "object") {
    el.content = doc;
    el.value = "";
  } else {
    el.content = null;
    el.value = html;
  }
  el.readonly = true;
}

watch(
  [notionContentDoc, notionValueProp, articleEditorInstanceKey],
  () => {
    nextTick(syncNotionCustomElementProps);
  },
  { deep: true }
);

onMounted(() => {
  nextTick(syncNotionCustomElementProps);
});

const mappedQuestions = computed(() =>
  (currentSection.value.questions || []).map((question, questionIndex) => {
    const id = question.id ?? `question-${questionIndex + 1}`;
    const selectedIds = localAnswers.value[id] || question.selectedOptionIds || [];
    const answers = (question.options || []).map((option, optionIndex) => {
      const optionId = option.id ?? `${id}-option-${optionIndex + 1}`;
      return {
        id: optionId,
        text: option.answerText || option.answer_text || option.text || "",
        isSelected: selectedIds.includes(optionId),
        isCorrect: Boolean(option.isCorrect ?? option.is_correct),
      };
    });
    const isAnswered = selectedIds.length > 0;
    const verdict = questionResult(question, id);
    // Review of a passed quiz has no in-session verdicts — treat every question as correct.
    const result = props.readonly
      ? "correct"
      : verdict === "incorrect" && isAnswered
        ? ""
        : verdict;

    return {
      id,
      key: id,
      position: questionIndex,
      text: question.text || "",
      answers,
      result,
      resultMessage: question.resultMessage || "",
      isLocked: props.readonly || result === "correct",
      isAnswered,
      isCorrect:
        answers.length > 0 && answers.every((answer) => answer.isCorrect === answer.isSelected),
    };
  })
);

/** After submit, keep incorrect questions first in their original sequence. */
const normalizedQuestions = computed(() => {
  const mapped = mappedQuestions.value;
  if (!quizSubmitted.value) return mapped;
  return [...mapped].sort(
    (a, b) => Number(a.result === "correct") - Number(b.result === "correct")
  );
});

const answeredQuestionsCount = computed(
  () => normalizedQuestions.value.filter((question) => question.isAnswered).length
);

const correctQuestionsCount = computed(() => {
  if (props.readonly) return normalizedQuestions.value.length;
  return normalizedQuestions.value.filter((question) => question.result === "correct").length;
});

const quizEvaluated = computed(
  () => quizSubmitted.value || normalizedQuestions.value.some((question) => question.result)
);

const quizPassed = computed(
  () =>
    normalizedQuestions.value.length > 0 &&
    normalizedQuestions.value.every((question) => question.result === "correct")
);

const quizStatus = computed(() => {
  if (props.readonly || quizPassed.value) return "passed";
  if (
    normalizedQuestions.value.length > 0 &&
    answeredQuestionsCount.value === normalizedQuestions.value.length
  ) {
    return "ready";
  }
  return quizEvaluated.value ? "failed" : "answering";
});

const inspectionSteps = computed(() =>
  (currentSection.value.steps || []).map((step, index) => ({
    key: step.id ?? `inspection-step-${index + 1}`,
    position: index + 1,
    result: normalizeStepResult(step.result ?? step.verdict),
    comment: step.comment || "",
    inspectedByName: step.inspectedByName || step.inspected_by_name || "",
    inspectedAt: step.inspectedAt || step.inspected_at || "",
    competenceCategory: step.competenceCategory || step.competence_category || "knowledge",
    inspectorAction: step.inspectorAction || step.inspector_action || "",
    lookFor: step.lookFor || step.look_for || "",
    passCriteria: step.passCriteria || step.pass_criteria || "",
    failCriteria: step.failCriteria || step.fail_criteria || "",
    timestamp: step.timestamp || step.timestamp_ref || "",
    videoJumpUrl: step.videoJumpUrl || step.video_jump_url || "",
  }))
);

/** Not-applicable has no design yet, so it keeps the status alert (and the legacy cards). */
const inspectionCardV2Active = computed(
  () =>
    inspectionCardV2Enabled.value &&
    props.inspectionStatus !== "not_applicable" &&
    inspectionSteps.value.length > 0
);

const inspectionStepStates = computed(() => inspectionSteps.value.map((step) => step.result));

const INSPECTION_HELPER_TEXT = {
  pending: "Please reach out to a certified coach of this responsibility to schedule an inspection.",
  failed: "Prepare and ask again for inspection.",
};

const inspectionHelperText = computed(() => INSPECTION_HELPER_TEXT[props.inspectionStatus] || "");

const showAvailableInspectors = computed(
  () => ["pending", "failed"].includes(props.inspectionStatus) && props.inspectors.length > 0
);

const showInspectedBy = computed(
  () => props.inspectionStatus === "passed" && props.inspectorName.trim() !== ""
);

const inspectorFirstName = computed(() => props.inspectorName.trim().split(/\s+/)[0] || "");
const inspectorLastName = computed(() => props.inspectorName.trim().split(/\s+/).slice(1).join(" "));

const inspectedByText = computed(() =>
  props.inspectionDate
    ? `Inspected by ${props.inspectorName} on ${props.inspectionDate}`
    : `Inspected by ${props.inspectorName}`
);

const actionDisabled = computed(() => {
  if (props.disabled) return true;
  if (props.readonly) return false;
  return isQuiz.value && normalizedQuestions.value.length > 0 && !quizPassed.value;
});

function normalizeStepResult(value) {
  return value === "passed" || value === "failed" ? value : "not_yet_inspected";
}

function defaultSectionTitle(type) {
  const normalizedType = String(type).toLowerCase();
  return (
    {
      article: "Read Article",
      video: "Watch this Video",
      quiz: "Take the Quiz",
      result: "Your result",
      inspection: "Inspection Protocol",
      "inspector-only":
        "This checkpoint will be inspected by a coach using only criteria as a guide",
    }[normalizedType] || "Checkpoint"
  );
}

function questionResult(question, id) {
  if (question.showResults) return question.isCorrect === false ? "incorrect" : "correct";
  return questionVerdicts.value[id] || "";
}

function buildQuizPayload() {
  return mappedQuestions.value.map((question) => ({
    id: question.id,
    text: question.text,
    isCorrect: question.isCorrect,
    selectedOptionIds: question.answers
      .filter((answer) => answer.isSelected)
      .map((answer) => answer.id),
  }));
}

function handleAnswerChange(questionId, { answerId, isSelected }) {
  if (props.readonly || props.disabled) return;

  const existing = localAnswers.value[questionId] || [];
  const next = isSelected
    ? [...new Set([...existing, answerId])]
    : existing.filter((id) => id !== answerId);

  localAnswers.value = {
    ...localAnswers.value,
    [questionId]: next,
  };

  emit("answer-change", { questionId, optionId: answerId, selected: isSelected });
  emit("update:selected-answers", { ...localAnswers.value });
}

function handleQuizSubmit() {
  if (props.disabled || props.readonly) return;

  const payload = buildQuizPayload();
  const verdicts = { ...questionVerdicts.value };
  const nextAnswers = { ...localAnswers.value };

  // A failed question is reopened for another attempt, correct ones stay locked
  mappedQuestions.value.forEach((question) => {
    verdicts[question.id] = question.isCorrect ? "correct" : "incorrect";
    if (!question.isCorrect) nextAnswers[question.id] = [];
  });

  questionVerdicts.value = verdicts;
  quizSubmitted.value = true;
  localAnswers.value = nextAnswers;

  emit("quiz-submit", payload);
  emit("update:selected-answers", { ...localAnswers.value });
}

function handlePrevious() {
  if (clampedActiveStep.value <= 1) return;

  activeStep.value = clampedActiveStep.value - 1;
  emit("previous", activeStep.value);
  emit("update:current-step", activeStep.value);
}

function handleAction() {
  if (actionDisabled.value) return;

  if (isLastStep.value) {
    emit("complete");
    emit("close");
    return;
  }

  goToNextStep();
}

function goToNextStep() {
  activeStep.value = clampedActiveStep.value + 1;
  emit("next", activeStep.value);
  emit("update:current-step", activeStep.value);
}
</script>

<style scoped>
.employee-checkpoint {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  border-left: 1px solid #eaecf0;
  background: #ffffff;
  box-shadow:
    0 20px 24px -4px rgba(16, 24, 40, 0.08),
    0 8px 8px -4px rgba(16, 24, 40, 0.03);
  box-sizing: border-box;
  color: #344054;
  font-family: Inter, sans-serif;
}

.employee-checkpoint__navigator {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 16px;
}

.employee-checkpoint__navigator-main {
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.employee-checkpoint__steps {
  display: flex;
  flex: 0 0 343px;
  justify-content: center;
  margin-left: auto;
  margin-right: 55px;
}

.employee-checkpoint__next--disabled:disabled {
  background: #f2f4f7 !important; /* override StandardButton primary fill */
  border-color: #eaecf0 !important; /* override StandardButton border */
  color: #98a2b3 !important; /* override StandardButton label color */
  cursor: not-allowed !important; /* override StandardButton pointer */
}

.employee-checkpoint__next--disabled:disabled :deep(svg path),
.employee-checkpoint__next--disabled:disabled :deep([stroke]) {
  stroke: #98a2b3 !important; /* override StandardButton chevron stroke */
}

.employee-checkpoint__section-title {
  margin: 0;
  overflow: hidden;
  color: #344054;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.employee-checkpoint__section-title--note {
  overflow: visible;
  white-space: normal;
}

.employee-checkpoint__content {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 16px 16px 24px;
  box-sizing: border-box;
}

.employee-checkpoint__body {
  flex: 1 1 auto;
  min-height: 0;
  padding: 24px 16px;
  overflow-y: auto;
}

.employee-checkpoint__body--lesson-card {
  padding: 16px;
}

/* Video chapter cards — timestamp, chapter name and description inside a hairline card */
.employee-checkpoint__body :deep(.microlearning-chapter-card__content) {
  gap: 5px;
  padding: 10px;
  border: 0.5px solid #d0d5dd;
  border-radius: 4px;
}

.employee-checkpoint__body :deep(.microlearning-chapter-card__timestamp) {
  padding-left: 5px;
  color: #2c0cfa;
  font-size: 14px;
  line-height: 20px;
}

.employee-checkpoint__body :deep(.microlearning-chapter-card__bookmark) {
  display: none;
}

.employee-checkpoint__body :deep(.microlearning-chapter-card__title-row),
.employee-checkpoint__body :deep(.microlearning-chapter-card__subtitle),
.employee-checkpoint__body :deep(.microlearning-chapter-card__description) {
  padding: 0 5px;
}

.employee-checkpoint__body :deep(.microlearning-chapter-card__title) {
  color: #475467;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
}

.employee-checkpoint__body :deep(.microlearning-chapter-card__subtitle),
.employee-checkpoint__body :deep(.microlearning-chapter-card__description) {
  color: #475467;
  font-size: 14px;
  line-height: 20px;
}

.employee-checkpoint__article {
  display: flex;
  flex-direction: column;
  gap: 16px;
  color: #475467;
  font-size: 14px;
  line-height: 20px;
}

.employee-checkpoint__article-editor {
  margin: 0;
  padding: 0;
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
}

.employee-checkpoint__article-heading {
  margin: 0;
  color: #344054;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
}

.employee-checkpoint__result {
  display: flex;
  flex-direction: column;
}

.employee-checkpoint__quiz,
.employee-checkpoint__inspection,
.employee-checkpoint__inspection-steps {
  display: flex;
  flex-direction: column;
}

.employee-checkpoint__quiz,
.employee-checkpoint__inspection {
  gap: 22px;
}

.employee-checkpoint__quiz-alert {
  flex-shrink: 0;
  padding: 24px 16px 0;
}

.employee-checkpoint__inspection-steps {
  gap: 22px;
}

.employee-checkpoint__inspection-summary {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
}

.employee-checkpoint__inspection-progress {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.employee-checkpoint__inspection-helper {
  margin: 0;
  color: #475467;
  font-size: 14px;
  line-height: 20px;
}

.employee-checkpoint__inspection-people {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 12px;
  color: #101828;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.employee-checkpoint__inspection-steps :deep(.instruction-step-card) {
  width: 100%;
  padding: 10px;
  border: 0.5px solid #d0d5dd;
  border-radius: 4px;
  box-sizing: border-box;
}

.employee-checkpoint__empty {
  padding: 48px 16px;
  color: #667085;
  font-size: 14px;
  text-align: center;
}

.employee-checkpoint__video-card {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
}

.employee-checkpoint__video-title {
  background-color: #f9fafb;
  padding: 16px 24px;
  border-bottom: 1px solid #eaecf0;
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.employee-checkpoint__video-title-text {
  font-family: Inter, sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: #344054;
}

.employee-checkpoint__video-close {
  color: #667085;
}

.employee-checkpoint__video-content {
  padding: 24px;
  background-color: #ffffff;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.employee-checkpoint__video-iframe-wrap {
  width: 100%;
  height: 400px;
  margin: 0 auto;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.employee-checkpoint__video-iframe {
  width: 100%;
  height: 100%;
  border: none;
  border-radius: 8px;
}

.employee-checkpoint__video-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 200px;
  color: #667085;
}

@media (max-width: 760px) {
  .employee-checkpoint__navigator {
    flex-wrap: wrap;
  }

  .employee-checkpoint__navigator-main {
    flex-wrap: wrap;
  }

  .employee-checkpoint__steps {
    flex: 1 1 auto;
    margin-left: 0;
    margin-right: 0;
  }
}
</style>
